/**
 * BoardEditor — standalone board page: title, script-linking and PNG export.
 * The canvas + autosave live in components/BoardCanvas (shared with the
 * in-editor board tab).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { exportToBlob } from "@excalidraw/excalidraw";
import { api, formatApiError } from "@/lib/api";
import { BoardCanvas } from "@/components/BoardCanvas";
import { toast } from "sonner";
import { ArrowLeft, Link2, Download, Check, Loader2, Shapes } from "lucide-react";

export default function BoardEditor() {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState({ saving: false, savedAt: null });
  const [scripts, setScripts] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkedIds, setLinkedIds] = useState([]);

  const apiRef = useRef(null);

  // load board
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/boards/${boardId}`);
        if (!alive) return;
        setBoard(data.board);
        setTitle(data.board.title || "Untitled Board");
        setLinkedIds(data.board.script_ids || []);
      } catch (e) {
        if (e.response?.status === 404) { toast.error("Board not found"); navigate("/app/boards"); return; }
        if (e.response?.status === 403) { navigate("/app/boards"); return; }
        toast.error(formatApiError(e.response?.data?.detail) || "Couldn't open the board");
      }
    })();
    return () => { alive = false; };
  }, [boardId, navigate]);

  const saveTitle = useCallback(async () => {
    const t = (title || "").trim() || "Untitled Board";
    if (t === board?.title) return;
    try { await api.put(`/boards/${boardId}`, { title: t }); setBoard((b) => ({ ...b, title: t })); }
    catch { toast.error("Couldn't rename the board."); }
  }, [title, board, boardId]);

  const openLink = useCallback(async () => {
    setLinkOpen((o) => !o);
    if (scripts.length === 0) {
      try { const { data } = await api.get("/scripts"); setScripts(data.scripts || []); }
      catch { /* ignore */ }
    }
  }, [scripts.length]);

  const toggleScript = useCallback(async (sid) => {
    const next = linkedIds.includes(sid) ? linkedIds.filter((x) => x !== sid) : [...linkedIds, sid];
    setLinkedIds(next);
    try { await api.put(`/boards/${boardId}`, { script_ids: next }); }
    catch { toast.error("Couldn't update linked scripts."); }
  }, [linkedIds, boardId]);

  const exportPng = useCallback(async () => {
    const el = apiRef.current;
    if (!el) return;
    const elements = el.getSceneElements();
    if (!elements.length) { toast.info("Add something to the board first."); return; }
    try {
      const blob = await exportToBlob({
        elements, files: el.getFiles(),
        appState: { ...el.getAppState(), exportBackground: true },
        mimeType: "image/png",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(title || "board").replace(/[^a-z0-9-]+/gi, "_")}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  }, [title]);

  if (!board) {
    return (
      <div className="h-screen flex items-center justify-center bg-[var(--canvas)]">
        <p className="text-[var(--ink-muted)] text-sm" data-testid="board-loading">Opening your board…</p>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[var(--canvas)]">
      {/* top bar */}
      <div className="h-12 shrink-0 border-b border-[var(--line)] bg-[var(--canvas)] flex items-center justify-between px-3 gap-3 z-20">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate("/app/boards")} className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" title="Back to boards" data-testid="board-back-btn">
            <ArrowLeft size={17} />
          </button>
          <Shapes size={16} className="text-[var(--brand)] shrink-0" />
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            className="bg-transparent font-medium text-[var(--ink-primary)] outline-none min-w-0 w-56 sm:w-72 truncate focus:border-b focus:border-[var(--brand)]"
            data-testid="board-title-input"
          />
          <span className="text-[11px] text-[var(--ink-muted)] hidden sm:inline-flex items-center gap-1" data-testid="board-save-status">
            {status.saving ? <><Loader2 size={12} className="animate-spin" /> Saving…</> : status.savedAt ? <><Check size={12} /> Saved</> : null}
          </span>
        </div>
        <div className="flex items-center gap-1.5 relative">
          <button onClick={openLink} className={`btn-ghost text-sm inline-flex items-center gap-1.5 ${linkedIds.length ? "text-[var(--brand)]" : ""}`} data-testid="board-link-btn">
            <Link2 size={15} /> <span className="hidden sm:inline">Link scripts</span>{linkedIds.length ? ` (${linkedIds.length})` : ""}
          </button>
          <button onClick={exportPng} className="btn-ghost text-sm inline-flex items-center gap-1.5" data-testid="board-export-btn">
            <Download size={15} /> <span className="hidden sm:inline">PNG</span>
          </button>
          {linkOpen && (
            <div className="absolute top-11 right-0 w-72 max-h-80 overflow-auto bg-[var(--paper)] border border-[var(--line)] rounded-lg shadow-xl p-2 z-30" data-testid="board-link-panel">
              <p className="text-[11px] uppercase tracking-widest text-[var(--ink-muted)] px-2 py-1">Link this board to scripts</p>
              {scripts.length === 0 ? (
                <p className="text-sm text-[var(--ink-muted)] px-2 py-3">No scripts yet.</p>
              ) : scripts.map((s) => (
                <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[var(--warm-accent)] cursor-pointer text-sm" data-testid={`board-link-script-${s.id}`}>
                  <input type="checkbox" checked={linkedIds.includes(s.id)} onChange={() => toggleScript(s.id)} className="accent-[var(--brand)]" />
                  <span className="truncate text-[var(--ink-primary)]">{s.title}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* canvas */}
      <div className="flex-1 relative min-h-0">
        <BoardCanvas
          board={board}
          onApi={(a) => { apiRef.current = a; }}
          onStatus={(s) => setStatus((p) => ({ ...p, ...s }))}
        />
      </div>
    </div>
  );
}
